import dayjs from 'dayjs';

import {i18n} from '@yourcoach/i18n';

import type {CourseApiModel} from '../../../api/course';
import type {Nullable} from '../../utility-types';

import {datetimeObjToISOString} from '../../datetime';
import {isDefined} from '../../typecheck';
import {isMeCoachForEntity} from '../isMeCoachForEntity';
import {isCourseEvergreen} from './isCourseEvergreen';

export const getCourseDurationString = (
  course?: Nullable<Partial<CourseApiModel>>,
) => {
  if (isDefined(course)) {
    /**
     * A course can have a nullable start_date in the scope of an individual program
     * When a client joins the individual program, the backend creates a course without dates
     */
    const isCourseHasStartDate = isDefined(course.start_date);

    if (isCourseHasStartDate) {
      return computeString(course);
    } else {
      /**
       * @i18n_parser
       * t('shared:label.course_no_start_date.coach')
       * t('shared:label.course_no_start_date.client')
       */
      return i18n.t(
        `shared:label.course_no_start_date.${isMeCoachForEntity(course) ? 'coach' : 'client'}`,
      );
    }
  }

  return '';
};

const computeString = (course: Partial<CourseApiModel>) => {
  const isEvergreen = isCourseEvergreen(course);

  const startDate = dayjs(datetimeObjToISOString(course.start_date));
  const fullCycleEndDate = course.program_end_date
    ? dayjs(datetimeObjToISOString(course.program_end_date))
    : null;
  const endDate = dayjs(datetimeObjToISOString(course.end_date));

  if (isEvergreen) {
    return i18n.t('shared:label.evergreen_course_date', {
      date: startDate.format('ll').replace(/\s/g, '\u00A0'),
    });
  }

  return `${startDate.format('ll').replace(/\s/g, '\u00A0')} — ${(
    fullCycleEndDate ?? endDate
  )
    .format('ll')
    .replace(/\s/g, '\u00A0')}`;
};
